import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Loader2, CheckCircle2, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQ_LABEL = {
  instant: 'Instant — as soon as we have something for you',
  weekly: 'Weekly digest',
  monthly: 'Monthly digest',
  never: 'Never — unsubscribe me',
};

/**
 * Public unsubscribe & preference center. Reachable via the per-recipient
 * link in every marketing email — no login required.
 *
 *   /unsubscribe?token=...                  → full preference page
 *   /unsubscribe?token=...&action=unsub_all → submits the master opt-out
 *                                             immediately, then renders the
 *                                             preference page so the user
 *                                             can re-tune if they change
 *                                             their mind.
 */
export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const action = params.get('action');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [catalogue, setCatalogue] = useState({ event_types: [], music_genres: [], frequencies: [] });
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (!token) {
          setError('Missing unsubscribe token. Please use the link from your email.');
          setLoading(false);
          return;
        }
        const [cRes, pRes] = await Promise.all([
          axios.get(`${API}/email-preferences/catalogue`),
          axios.get(`${API}/email-preferences/public/${token}`),
        ]);
        if (cancelled) return;
        setCatalogue(cRes.data);
        let p = pRes.data;
        // Honor ?action=unsub_all immediately so the link feels instant.
        if (action === 'unsub_all' && !p.unsubscribed) {
          const r = await axios.put(`${API}/email-preferences/public/${token}`, {
            unsubscribed: true, frequency: 'never',
          });
          p = r.data;
          toast.success("You've been unsubscribed from all marketing emails.");
        }
        setPrefs(p);
      } catch (e) {
        setError(e.response?.data?.detail || 'This link is invalid or has expired.');
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [token, action]);

  const updateField = (patch) => setPrefs(prev => ({ ...prev, ...patch }));
  const toggleListItem = (key, id) => {
    setPrefs(prev => {
      const cur = new Set(prev[key] || []);
      if (cur.has(id)) cur.delete(id); else cur.add(id);
      return { ...prev, [key]: Array.from(cur) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/email-preferences/public/${token}`, {
        unsubscribed: !!prefs.unsubscribed,
        frequency: prefs.frequency,
        event_types: prefs.event_types || [],
        music_genres: prefs.music_genres || [],
      });
      setPrefs(r.data);
      toast.success('Preferences saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save preferences');
    } finally { setSaving(false); }
  };

  const unsubAll = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/email-preferences/public/${token}`, {
        unsubscribed: true, frequency: 'never',
      });
      setPrefs(r.data);
      toast.success("You've been unsubscribed from all marketing emails.");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not unsubscribe');
    } finally { setSaving(false); }
  };

  const resubscribe = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/email-preferences/public/${token}`, {
        unsubscribed: false, frequency: 'instant',
      });
      setPrefs(r.data);
      toast.success('Welcome back! You can fine-tune your preferences below.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resubscribe');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950" data-testid="unsubscribe-loading">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
        <Card className="max-w-md w-full" data-testid="unsubscribe-error">
          <CardContent className="p-8 text-center">
            <Mail className="h-10 w-10 text-rose-500 mx-auto mb-3" />
            <h1 className="text-lg font-semibold mb-2">We couldn't find your preferences</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <p className="text-xs text-muted-foreground mt-4">Need help? Email <a className="text-indigo-500 underline" href="mailto:press@eventism.com">press@eventism.com</a></p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isUnsub = !!prefs?.unsubscribed;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4" data-testid="unsubscribe-page">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight">Email Preferences</h1>
          <p className="text-sm text-muted-foreground mt-1">{prefs.email}</p>
        </div>

        {isUnsub && (
          <Card className="border-rose-500/40 bg-rose-500/5" data-testid="unsubscribe-confirmed">
            <CardContent className="p-5 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-rose-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold">You're unsubscribed from all marketing emails.</p>
                <p className="text-xs text-muted-foreground mt-1">You'll still receive transactional emails (ticket confirmations, receipts, account-security notices).</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={resubscribe} disabled={saving} data-testid="resubscribe-btn">
                  Resubscribe and customize what I get
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!isUnsub && (
          <>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">How often should we email you?</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(catalogue.frequencies || []).map(f => (
                  <label key={f} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition ${prefs.frequency === f ? 'border-indigo-500 bg-indigo-500/5' : 'border-border hover:bg-muted/50'}`}>
                    <input type="radio" name="frequency" checked={prefs.frequency === f} onChange={() => updateField({ frequency: f })} data-testid={`freq-${f}`} />
                    <span className="text-sm">{FREQ_LABEL[f] || f}</span>
                  </label>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">What kinds of events do you love?</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(catalogue.event_types || []).map(opt => {
                    const on = (prefs.event_types || []).includes(opt.id);
                    return (
                      <button key={opt.id} type="button" onClick={() => toggleListItem('event_types', opt.id)} data-testid={`evt-${opt.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${on ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-background border-border hover:bg-muted'}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">Leave them all blank to receive every event update.</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Pick your music vibes</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(catalogue.music_genres || []).map(opt => {
                    const on = (prefs.music_genres || []).includes(opt.id);
                    return (
                      <button key={opt.id} type="button" onClick={() => toggleListItem('music_genres', opt.id)} data-testid={`genre-${opt.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${on ? 'bg-fuchsia-500 text-white border-fuchsia-500' : 'bg-background border-border hover:bg-muted'}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        <div className="flex items-center justify-between gap-3 pt-2">
          {!isUnsub && (
            <Button variant="outline" className="text-rose-500 border-rose-500/40 hover:bg-rose-500/10" onClick={unsubAll} disabled={saving} data-testid="unsub-all-btn">
              Unsubscribe from all
            </Button>
          )}
          <div className="ml-auto">
            {!isUnsub && (
              <Button onClick={save} disabled={saving} data-testid="save-prefs-btn">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : 'Save preferences'}
              </Button>
            )}
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground text-center mt-6">
          Eventism · Denver, CO · <a href="mailto:press@eventism.com" className="underline">press@eventism.com</a>
        </p>
      </div>
    </div>
  );
}
